import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { heroContent } from "@/data/mock";

const MEMBERS_AREA_URL = "https://app.lifely.guide";

// 9 images for the grid - center image (index 4) is the hero
// All images use consistent 800x600 crop to prevent layout shifting
const gridImages = [
  {
    // 1. MacBook Pro beside clear drinking glass (N1gUD_dCvJE) - moved from position 7
    src: "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5?w=800&h=600&q=80&auto=format&fit=crop",
    alt: "MacBook Pro beside clear drinking glass on brown wooden table",
  },
  {
    // 2. Person using laptop (Hcfwew744z4)
    src: "https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?w=800&h=600&q=80&auto=format&fit=crop",
    alt: "Person using laptop computer",
  },
  {
    // 3. Silhouette of man and kid on seashore (uSj_ce8vtyE) - moved from position 9
    src: "https://images.unsplash.com/photo-1506456331400-7088248a8db1?w=800&h=600&q=80&auto=format&fit=crop",
    alt: "Silhouette of man and kid on seashore",
  },
  {
    // 4. Woman holding man and toddler hands (5NLCaz2wJXE)
    src: "https://images.unsplash.com/photo-1475503572774-15a45e5d60b9?w=800&h=600&q=80&auto=format&fit=crop",
    alt: "Woman holding man and toddler hands during daytime",
  },
  {
    // 5. Person typing on silver MacBook (kUqqaRjJuw0) - CENTER/HERO
    src: "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=800&h=600&q=80&auto=format&fit=crop",
    alt: "Person typing on silver MacBook",
  },
  {
    // 6. White wooden house between trees (sejLyCD2UQE)
    src: "https://images.unsplash.com/photo-1434082033009-b81d41d32e1c?w=800&h=600&q=80&auto=format&fit=crop",
    alt: "White wooden house between trees",
  },
  {
    // 7. Bitcoin on gold nuggets (rhm7H8X5J98)
    src: "https://images.unsplash.com/photo-1623227413711-25ee4388dae3?w=800&h=600&q=80&auto=format&fit=crop",
    alt: "Bitcoin sitting on top of a pile of gold nuggets",
  },
  {
    // 8. Blue gray and yellow coupe (igaoYBHBDws)
    src: "https://images.unsplash.com/photo-1534093607318-f025413f49cb?w=800&h=600&q=80&auto=format&fit=crop",
    alt: "Blue gray and yellow coupe on gray floor",
  },
  {
    // 9. User uploaded image
    src: "https://images.unsplash.com/photo-1501243249600-0560abe6e5a3?w=800&h=600&q=80&auto=format&fit=crop",
    alt: "Image 9",
  },
];

export const ZoomGridHero = () => {
  const containerRef = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });

  // Grid scale: starts at 3 (zoomed in to show center) -> 0.7 (full grid visible with more of outer images)
  const gridScale = useTransform(scrollYProgress, [0, 0.6], [3, 0.7]);
  
  // Hero text opacity: visible at start -> fades out
  const textOpacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  
  // Overlay opacity: starts dark -> lightens to show grid
  const overlayOpacity = useTransform(scrollYProgress, [0, 0.5], [0.7, 0]);

  return (
    <section
      ref={containerRef}
      className="relative h-[200vh]"
      data-testid="zoom-grid-hero"
    >
      {/* Sticky container that stays in view during scroll */}
      <div className="sticky top-0 h-screen w-full overflow-hidden">
        {/* Scaled grid container */}
        <motion.div
          style={{ scale: gridScale }}
          className="absolute inset-0 flex items-center justify-center origin-center"
        >
          {/* 3x3 Grid - taller to show more of top/bottom rows */}
          <div className="grid grid-cols-3 gap-2 w-[160vw] h-[180vh] max-w-none">
            {gridImages.map((image, index) => (
              <div
                key={index}
                className="relative overflow-hidden"
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover"
                  loading={index === 4 ? "eager" : "lazy"}
                />
              </div>
            ))}
          </div>
        </motion.div>

        {/* Dark overlay for text readability - fades out on scroll */}
        <motion.div
          style={{ opacity: overlayOpacity }}
          className="absolute inset-0 bg-[#2D2A26] pointer-events-none"
        />

        {/* Hero text content - fades out on scroll */}
        <motion.div
          style={{ opacity: textOpacity }}
          className="absolute inset-0 flex items-center justify-center z-10"
        >
          <div className="max-w-4xl mx-auto px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/70 mb-8 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 border border-white/10"
            >
              <div className="w-1.5 h-1.5 rounded-full bg-[#C17B5A]" />
              A living map of what matters
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
              className="font-serif text-[2.8rem] sm:text-[3.5rem] md:text-[4.2rem] lg:text-[5rem] leading-[1.04] tracking-tight text-white"
            >
              {heroContent.heading}
              <br />
              <span className="text-[#E8A882]">{heroContent.headingAccent}</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="mt-7 text-lg text-white/70 leading-relaxed max-w-2xl mx-auto"
            >
              {heroContent.subheading}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="mt-9 flex flex-wrap justify-center gap-4"
            >
              <Link to={heroContent.primaryCta.path}>
                <Button className="h-12 px-8 text-sm bg-white text-[#2D2A26] hover:bg-white/90 rounded-full font-medium">
                  {heroContent.primaryCta.label}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href={MEMBERS_AREA_URL}>
                <Button
                  variant="outline"
                  className="h-12 px-8 text-sm rounded-full border-white/30 text-white hover:bg-white/10 font-medium"
                >
                  {heroContent.secondaryCta.label}
                  <ArrowUpRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </motion.div>
          </div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          style={{ opacity: textOpacity }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 hidden md:flex z-10"
        >
          <span className="text-[0.65rem] text-white/40 tracking-[0.25em] uppercase">
            Scroll
          </span>
          <motion.div
            animate={{ y: [0, 6, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            className="w-5 h-8 rounded-full border-[1.5px] border-white/25 flex items-start justify-center pt-1.5"
          >
            <div className="w-1 h-1.5 rounded-full bg-white/40" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
